import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PrivatePage } from "components/layouts/PrivatePage";
import { Patient } from "components/views/PrescriptionDetails/Patient";
import { RecipeCheck } from "components/views/PrescriptionDetails/RecipeCheck";
import { AddedMedications } from "components/views/PrescriptionDetails/AddedMedications";
import { RejectRecipe } from "components/views/PrescriptionDetails/RejectRecipe";
import { FooterActions } from "components/views/PrescriptionDetails/FooterActions";

import {
    getOnePrescription,
    setAnalysesStatus,
    AnalyzeStatus,
    getReprovalReasons,
    getPrescriptionImage,
} from "services/api/prescription/prescriptionService";

import { useAppDispatch, useAppSelector } from "hooks/store";

import {
    updateApprovedProducts,
    resetApprovedProducts,
    resetDetails,
    resetReprovalReasons,
    setDetails,
    setLoadingDetailPageOn,
    setLoadingDetailPageOff,
} from "store/modules/prescriptionDetail";

import {
    verifyAnalysisStatus,
    handleAnalysisStatus,
} from "services/api/recipeStatus/recipeStatusService";

import { getUserBySocialId } from "services/api/user/userService";
import { toast, Toaster } from "react-hot-toast";
import { Toast, ToastType } from "components/widgets/atoms/Toast";

import moment from "moment";

import {
    setImageLoadingOff,
    setImageLoadingOn,
    setLoadingOff,
    setLoadingOn,
} from "store/modules/loader";

import { Loader } from "components/widgets/molecules/Loader";

import { useDebouncedEffect } from "hooks/useDebouncedEffect";
import { useBeneficiaries } from "hooks/useBeneficiaries";
import { RejectIA } from "components/views/PrescriptionDetails/RejectIA";

export function PrescriptionDetail() {
    const dispatch = useAppDispatch();
    const { objectId } = useParams();
    const navigate = useNavigate();
    const [councilWarn, setCouncilWarn] = useState<Boolean>(false);
    const { loadingDetailPage } = useAppSelector((state) => state.prescriptionDetail); 
    const [reasonsRejectIa, setReasonsRejectIa] = useState<Array<String | null>>([]);
    const [isPageReady, setIsPageReady] = useState(false);
    const hasInitializedRef = useRef(false);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const { username } = useAppSelector((state) => state.login);
    const { data: { files, socialId, customerId, cardNumber } } = useAppSelector((state) => state.prescriptionDetail);

    let imageTryCounter = 1;

    // Libera o lock e descarta qualquer toast pendente ao sair da receita
    useEffect(() => {
        return () => {
            toast.dismiss("invalid");
            setAnalysesStatus({
                ObjectId: objectId || "",
                Username: username,
                Status: AnalyzeStatus.Analysed,
            });
        };
    }, [objectId, username]);

    const { data, isLoading: isLoadingPrescription, error } = useQuery({
        queryKey: [`prescription${objectId}`],
        queryFn: () => getOnePrescription(objectId || ""),
        refetchOnWindowFocus: false,
    });

    const [isRecipePending, setRecipePending] = useState<boolean>(false);

    useEffect(() => {
        if (data) {
            const isPending = data.data.data.status.status === "AnalisyPending";
            setRecipePending(isPending);
        }
    }, [data]);

    const { data: reprovalReasonsData, isLoading: isLoadingReprovalReasons } = useQuery({
        queryKey: ["reprovalreasons"],
        queryFn: () => getReprovalReasons(),
        refetchOnWindowFocus: false,
    });

    const [imagesPath, setImagesPath] = useState([]);
    const { beneficiaries, isLoadingBeneficiaries } = useBeneficiaries(cardNumber, customerId);
    const [startAnalyseDateTime, setStartAnalyseDateTime] = useState<moment.Moment>();

    useEffect(() => {
        setStartAnalyseDateTime(moment().utc());
        dispatch(setImageLoadingOn());

        return () => {
            dispatch(resetApprovedProducts());
            dispatch(resetReprovalReasons());
        };
    }, [dispatch]);

    useEffect(() => {
        return () => {
            dispatch(resetDetails());
        };

    }, [dispatch]);


    const getImages = useCallback(async () => {
        dispatch(setImageLoadingOn());
        try {
            const fileNames = files?.map((file) => file.fileName);

            if (fileNames.length !== 0) {
                const { data } = await getPrescriptionImage(fileNames);

                const firebaseUrls = data.data?.map((image) => image.firebaseUrl);
                setImagesPath(firebaseUrls);
            } else {
                throw new Error("Sem imagem.");
            }
        } catch (err) {
            if (imageTryCounter === 3) return;

            imageTryCounter++;
            setTimeout(async () => {
                getImages();
            }, 7000);
        } finally {
            dispatch(setImageLoadingOff());
        }
    }, [dispatch, files, imageTryCounter]);

    const kickUser = useCallback((iaUser: boolean = false) => {
        setTimeout(() => {
            navigate("/");
        }, 3000);

        const message = iaUser ? "pela IA" : "por outro usuário";

        return toast.custom(
            (t) => (
                <Toast
                    t={t}
                    text={`A receita está em análise ${message}.`}
                    type={ToastType.Error}
                />
            ),
            { id: "invalid", position: "top-center" }
        );
    }, [navigate]);

    const fetchActiveAnalysis = useCallback(async () => {
        try {
            const body = {
                username,
                objectId,
                status: 1,
            };
            await handleAnalysisStatus(body);
        } catch (err) {}
    }, [objectId, username]);

    // Inicialização sequencial
    useEffect(() => {
        if (!data || !objectId || !username) return;
        if (hasInitializedRef.current) return;

        hasInitializedRef.current = true;
        let cancelled = false;
        const prescriptionData = data;

        async function initializePage() {
            const isPrescriptionPending = prescriptionData.data.data.status.status === "AnalisyPending";

            // Descartar qualquer toast residual de sessões anteriores neste navegador.
            toast.dismiss("invalid");

            // Passo 1: GET apenas para saber se JÁ somos donos do lock (ex: reload).
            let knownLockOwner: string | undefined;
            try {
                const verifyResponse = await verifyAnalysisStatus({ username, objectId });
                if (cancelled) return;

                const lockData = verifyResponse.data?.data;
                knownLockOwner = lockData?.username;

                if (lockData?.status === 1 && knownLockOwner === username) {
                    // Já somos o dono do lock (ex: reload) — apenas renderizar
                    setIsPageReady(true);
                    return;
                }
                // Mesmo que o GET mostre lock de outro usuário, prosseguir para o PATCH.
            } catch (verifyErr: any) {
                if (cancelled) return;
                knownLockOwner = verifyErr?.response?.data?.data?.username;
            }

            // Passo 2: PATCH 
            try {
                await setAnalysesStatus({
                    ObjectId: objectId || "",
                    Username: username,
                    Status: AnalyzeStatus.InAnalysis,
                });
                if (cancelled) return;
                setIsPageReady(true);
            } catch (patchErr: any) {
                if (cancelled) return;

                // servidor confirma lock ativo. Só expulsa se a receita for pendente.
                if (patchErr.response?.status === 423 && isPrescriptionPending) {
                    setIsPageReady(true);
                    kickUser(knownLockOwner?.toUpperCase() === "SANTOID");
                    return;
                }

                //para receita não-pendente, ou outro erro, abrir normalmente
                setIsPageReady(true);
            }
        }

        initializePage();

        return () => {
            cancelled = true;
            hasInitializedRef.current = false;
            setIsPageReady(false);
        };
    }, [data, objectId, username, kickUser]);

    useEffect(() => {
        const sendIsActive = setInterval(() => {
            fetchActiveAnalysis();
        }, 10 * 1000);

        return () => {
            clearInterval(sendIsActive);
        };
    }, [fetchActiveAnalysis]);

    useDebouncedEffect(
        () => {
            getImages();
        },
        [getImages],
        2000
    );

    useEffect(() => {
        const isLoading = isLoadingPrescription || isLoadingReprovalReasons;

        if (isLoading) {
            dispatch(setLoadingOn());
        } else {
            dispatch(setLoadingOff());
        }
    }, [dispatch, isLoadingPrescription, isLoadingReprovalReasons]);
    

    const formatDetails = useCallback(async (dataPayload) => {
        let formattedData = {...dataPayload.data}
        
        if(typeof formattedData.data.ocrData === 'string') {
            formattedData.data.ocrData = JSON.parse(formattedData.data.ocrData)

            let reasonsRejectIAStringArray: string[] = [];

            for (const reason of formattedData.data?.reasonsRejectIA || []) {
                if (reason) {
                    reasonsRejectIAStringArray.push(...Object.values(reason) as string[]);
                }
            }

            if(reasonsRejectIAStringArray)
                setReasonsRejectIa(reasonsRejectIAStringArray);
        }
        
        return formattedData
    }, [])

    const loadDataAndUpdatedDetails = useCallback(async () => {
        try {
            dispatch(setLoadingDetailPageOn())

            if (data && typeof data !== "function") {
                const isCurrentObjectId = data.data.data.objectId === objectId;

                if (isCurrentObjectId) {
                    const formattedDetails = await formatDetails(data)
                    dispatch(setDetails(formattedDetails));
                    if(formattedDetails.data.approvedProducts) {
                        dispatch(updateApprovedProducts(formattedDetails.data.approvedProducts))
                    }
                }
            }
        } catch (error) {
            console.error(error)
        } finally {
            dispatch(setLoadingDetailPageOff())
        }
    }, [data, dispatch, formatDetails, objectId])

    useEffect(() => {
        loadDataAndUpdatedDetails()
    }, [loadDataAndUpdatedDetails]);

    function validateWarnStatus(value) {
        setCouncilWarn(value);
    }

    if (isLoadingPrescription || isLoadingReprovalReasons || !isPageReady) {
        return <Loader />;
    }

    if (error) {
        return <div>erro</div>;
    }

    return (
        <PrivatePage>
            <section className="container mx-auto mb-20 mt-12 space-y-10">
            {(isRecipePending && reasonsRejectIa && reasonsRejectIa.length > 0) && (
                <RejectIA reasonsRejectIa={reasonsRejectIa} />
            )}

                <Patient beneficiaries={beneficiaries} isLoadingBeneficiaries={isLoadingBeneficiaries} />

                <RecipeCheck
                    imagesPath={imagesPath}
                    validateWarnStatus={validateWarnStatus}
                />

                <AddedMedications />
                <RejectRecipe reasonsData={reprovalReasonsData?.data.data} />
                <FooterActions
                    startAnalyseDateTime={startAnalyseDateTime}
                    councilWarn={councilWarn}
                />
            </section>
            {loadingDetailPage ? <Loader /> : <></>}
            <Toaster
                data-testid="snackbar"
                position="bottom-center"
                containerStyle={{
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                }}
                toastOptions={{
                    duration: 3000,
                }}
            />
        </PrivatePage>
    );
}
